import axios from 'axios';
import { BASE_URL } from 'constants/api';
import { stringify } from 'query-string';
import { NODE_TYPE } from 'constants/node';
import { MULTIPLE_FORM_DATA, SOMETHING_WENT_WRONG } from 'constants/pricing-management';

const chapterImages = { type: 'chapter_images' };

const RESTRICTED_NODE_TYPE = {
  LEARNING: 'LEARNING',
  RECAP: 'RECAP',
  DIAGNOSTIC: 'DIAGNOSTIC',
  LEVEL2: 'LEVEL2',
  LEVEL3: 'LEVEL3',
  PUZZLE_EASY: 'PUZZLE_EASY',
  PUZZLE_MEDIUM: 'PUZZLE_MEDIUM',
  PUZZLE_HARD: 'PUZZLE_HARD',
  REMEDIAL: 'REMEDIAL',
  SUBJECTIVE: 'SUBJECTIVE',
};

const IS_OPTIONAL_RESTRICTED_NODE_TYPE = {
  SUBJECTIVE: 'SUBJECTIVE',
  PUZZLE_EASY: 'PUZZLE_EASY',
  PUZZLE_MEDIUM: 'PUZZLE_MEDIUM',
  PUZZLE_HARD: 'PUZZLE_HARD',
  LEVEL2: 'LEVEL2',
  LEVEL3: 'LEVEL3',
};

const IS_TIMED_RESTRICTED_NODE_TYPE = {
  REVISION: 'REVISION',
  ASSESSMENT: 'ASSESSMENT',
  CHAPTER_ASSESSMENT: 'CHAPTER_ASSESSMENT',
  DIAGNOSTIC: 'DIAGNOSTIC',
};

export const NODE_BOUNDARY_CONDITION = {
  COMPLETE_IN: 'COMPLETE_IN',
  UNLOCK_AFTER: 'UNLOCK_AFTER',
  IS_OPTIONAL: 'IS_OPTIONAL',
  IS_TIMED: 'IS_TIMED',
};

export const dataURIToBlob = dataURI => {
  var byteString;
  if (dataURI.split(',')[0].indexOf('base64') >= 0)
    byteString = atob(dataURI.split(',')[1]);
  else byteString = unescape(dataURI.split(',')[1]);

  // separate out the mime component
  var mimeString = dataURI
    .split(',')[0]
    .split(':')[1]
    .split(';')[0];

  // write the bytes of the string to a typed array
  var ia = new Uint8Array(byteString.length);
  for (var i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  const blobOBJ = new Blob([ia], { type: mimeString });

  return blobOBJ;
};

export const jsonToBlob = json => {
  const data = JSON.stringify(json);

  return new Blob([data], {
    type: 'application/json',
  });
};

export const handleAWSS3Upload = (file, key, awsSignedKey, resolve, reject) => {
  const { awsKey, bucketName, policy, signature } = awsSignedKey || {};
  const formData = new FormData();
  const url = `https://${bucketName}.s3.amazonaws.com/`;
  const source = `${url}${key}`;

  formData.append('key', key);
  formData.append('AWSAccessKeyId', awsKey);
  formData.append('acl', 'public-read');
  formData.append('success_action_redirect', '');
  formData.append('policy', policy);
  formData.append('signature', signature);
  formData.append('Content-Type', file.type);

  formData.append('file', file);

  axios
    .request({
      method: 'POST',
      url,
      data: formData,
      headers: {
        encType: 'multipart/form-data',
      },
    })
    .then(async () => {
      // do something after upload finished
      console.log('TIMING: uploading finished', new Date());
      resolve(source);
    })
    .catch(err => {
      console.log('Board: sendProgressToAWS: post error', err);
      reject(err);
    });
};

export const loadScript = (() => {
  const promises = {};

  return (url, id) => {
    if (promises[id]) {
      return promises[id];
    }

    promises[id] = new Promise((resolve, reject) => {
      const existingElement = document.querySelector(`#${id}`);
      const body = document.getElementsByTagName('body')[0];
      const script = document.createElement('script');

      script.type = 'text/javascript';
      script.id = id;
      script.src = url;
      script.onreadystatechange = resolve;
      script.onload = resolve;
      script.onerror = event => {
        reject(new Error(`Failed to load ${event.target.src}`));
      };

      if (existingElement) {
        existingElement.remove();
      }

      body.appendChild(script);
    });

    promises[id]
      .then(() => {
        promises[id] = null;
      })
      .catch(() => {
        promises[id] = null;
      });

    return promises[id];
  };
})();

export const getAWSSignedKeyChapters = async () => {
  let response;
  const url = `${BASE_URL}/configs/aws-signed-key/?${stringify(chapterImages)}`;

  await fetch(url, {
    method: 'GET',
  })
    .then(r => r.json().then(data => ({ status: r.status, body: data })))
    .then(obj => {
      response = obj.body.value;
    });

  return response;
};

export const getAWSSignedKey = async query => {
  let response;
  const url = `${BASE_URL}/configs/aws-signed-key/?${stringify(query)}`;

  await fetch(url, {
    method: 'GET',
  })
    .then(r => r.json().then(data => ({ status: r.status, body: data })))
    .then(obj => {
      response = obj.body.value;
    });

  return response;
};

export const verifyNodeType = (nodeType, boundary) => {
  if (boundary === NODE_BOUNDARY_CONDITION.COMPLETE_IN) {
    if (RESTRICTED_NODE_TYPE[`${nodeType}`]) {
      return true;
    }
  }

  if (boundary === NODE_BOUNDARY_CONDITION.UNLOCK_AFTER) {
    if (RESTRICTED_NODE_TYPE[`${nodeType}`] || nodeType === NODE_TYPE.PRACTICE) {
      return true;
    }
  }

  if (boundary === NODE_BOUNDARY_CONDITION.IS_OPTIONAL) {
    if (IS_OPTIONAL_RESTRICTED_NODE_TYPE[`${nodeType}`]) {
      return true;
    }
  }

  if (boundary === NODE_BOUNDARY_CONDITION.IS_TIMED) {
    if (IS_TIMED_RESTRICTED_NODE_TYPE[`${nodeType}`]) {
      return true;
    }
  }

  return false;
};

export const CSVFormatApi = async (methodName, url, formData) => {
  let response;
  let error;

  await axios
    .request({
      method: methodName,
      url,
      data: formData,
      headers: {
        encType: MULTIPLE_FORM_DATA,
      },
    })
    .then(res => {
      response = res;
    })
    .catch(err => {
      const { response } = err || {};
      const { data } = response || {};
      const { error: dataError } = data || {};
      const { message } = dataError || {};

      error = message || SOMETHING_WENT_WRONG;
    });

  return { response, error };
};
